"use client";

import { useMemo } from "react";

import { DynamicDocumentInstructions, WorkflowAction } from "@/configuration/document-mappings/types";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { useDocumentManagementInstructions } from "@/src/lib/data-fetchers/document-management-instructions-fetchers";
import { useFaxLocationAssignment } from "@/src/lib/data-fetchers/location-assignment-fetcher";
import { isPassthroughAssignee } from "@/src/lib/ehr/customer/heartplace/passthrough-assignee";
import { tryGetEhrSystemId } from "@/src/lib/resolve-ehr-ids";
import { renderTemplate } from "@/src/lib/templating";
import type { StepOutputs } from "@/src/lib/workflows/steps/step-outputs";
import { Classification } from "@/src/models/constants/classifications";
import { Section } from "@/src/models/section";
import { Task } from "@/src/models/task";

// Groups whose staff location comes from a live eCW encounter lookup rather than static fax-number config.
const GROUPS_RESOLVING_FACILITY_ASYNC: ReadonlySet<string> = new Set([
  ConfigSubdomain.PA_CENTRAL_REGION,
  ConfigSubdomain.PA_EAST_REGION,
] satisfies readonly ConfigSubdomain[]);

export interface InstructionField {
  label: string;
  value: string;
}

export interface ProcessedInstructions {
  displayName: string;
  standardFields: InstructionField[];
  extraFields: InstructionField[];
  instructions: readonly string[] | null;
  note: string | null;
  noteLabel: string | null;
  statusLine: string | null;
  workflowAction: WorkflowAction | null;
  additionalWorkflow: readonly string[] | null;
  faxRecipientNumber: string | null;
  tags: readonly string[] | null;
  stepOutputs: StepOutputs | null;
  ccDescription: string | null;
  processInstructionText: (text: string) => string;
}

interface LocationAssignment {
  authLocation?: string | null;
  staffLocation?: string | null;
  locationPrefix?: string | null;
  faxRecipientNumber?: string | null;
  resolvedAssignment?: string | null;
  isLoading?: boolean;
}

function getGroupExtraFields(
  groupName: string,
  patientFamilyName: string | null | undefined,
  patientGivenName: string | null | undefined,
  patientEhrSystemId: string | null,
  ehrDocumentId: string | null | undefined
): InstructionField[] {
  switch (groupName) {
    case ConfigSubdomain.STRIDECARE:
      return [
        {
          label: "Patient Name",
          value: [patientFamilyName, patientGivenName].filter(Boolean).join(", ") || "N/A",
        },
        { label: "EHR ID", value: patientEhrSystemId || "N/A" },
        { label: "Document ID", value: ehrDocumentId || "N/A" },
      ];
    default:
      return [];
  }
}

function replaceLocationPlaceholder(text: string | null, locationValue: string | null): string {
  if (!text) {
    return locationValue || "";
  }

  if (!locationValue) {
    return text;
  }

  return text.replace(/\{\{?location\}\}?/g, locationValue);
}

export function useBuildInstructions(
  task: Task,
  section: Section,
  mapping: DynamicDocumentInstructions | null | undefined,
  stepOutputs: StepOutputs | null | undefined,
  ccDescription: string | null | undefined,
  location: LocationAssignment = {}
): ProcessedInstructions | null {
  const {
    authLocation = null,
    staffLocation = null,
    locationPrefix = null,
    faxRecipientNumber = null,
    resolvedAssignment = null,
    isLoading: isLocationLoading = false,
  } = location;
  const reviewedContent = section.reviewedContent;
  const patientFamilyName = task.patient?.familyName;
  const patientGivenName = task.patient?.givenName;
  const patientEhrSystemId = task.patient ? tryGetEhrSystemId(task.patient) : null;
  const ehrDocumentId = section.ehrDocumentId;

  return useMemo<ProcessedInstructions | null>(() => {
    if (!mapping) return null;

    const templateContent = reviewedContent || {};

    const group = { subdomain: mapping.group };

    const processInstructionText = (text: string): string => {
      const rendered = renderTemplate(text, templateContent, group);

      return replaceLocationPlaceholder(rendered, authLocation);
    };

    const isHeartplaceNonMrr = mapping.group === ConfigSubdomain.USHV_HEARTPLACE;
    const isPaReferralRequestFacility =
      (mapping.group === ConfigSubdomain.PA_EAST_REGION || mapping.group === ConfigSubdomain.PA_CENTRAL_REGION) &&
      mapping.classification === Classification.REFERRAL_REQUEST;

    const standardFields: InstructionField[] = [
      ...(!isHeartplaceNonMrr
        ? [{ label: "Workflow Type", value: renderTemplate(mapping.workflowType, templateContent, group) }]
        : []),
      { label: "Priority", value: renderTemplate(mapping.priority, templateContent, group) },
      { label: "Document type", value: mapping.displayName },
      { label: "Document label", value: renderTemplate(mapping.documentLabel, templateContent, group) },
      { label: "Input date", value: mapping.inputDate },
      {
        label: isHeartplaceNonMrr ? "Folder" : "Upload to folder",
        value: renderTemplate(mapping.folder || "", templateContent, group),
      },
      {
        label: "Assign to",
        value:
          resolvedAssignment ??
          (authLocation && isPassthroughAssignee(authLocation) && /\{\{?location\}\}?/.test(mapping.assignedTo ?? "")
            ? authLocation
            : replaceLocationPlaceholder(
                mapping.assignedTo?.replace(/\{\{?authPrefix\}\}?/g, locationPrefix ?? "HP") ?? null,
                authLocation
              )),
      },
    ].filter((field) => field.value);

    if (mapping.markAsReviewed != null) {
      standardFields.push({ label: "Mark as reviewed", value: mapping.markAsReviewed ? "Yes" : "No" });
    }

    const extraFields: InstructionField[] = [
      ...getGroupExtraFields(mapping.group, patientFamilyName, patientGivenName, patientEhrSystemId, ehrDocumentId),
    ];

    if (staffLocation) {
      const staffLocationValue =
        isHeartplaceNonMrr && locationPrefix ? `${locationPrefix} ${staffLocation}` : staffLocation;

      extraFields.push({
        label: isHeartplaceNonMrr ? "Fax sent to" : isPaReferralRequestFacility ? "Facility" : "Staff Location",
        value: staffLocationValue,
      });
    } else if (isLocationLoading && GROUPS_RESOLVING_FACILITY_ASYNC.has(mapping.group)) {
      extraFields.push({
        label: isPaReferralRequestFacility ? "Facility" : "Staff Location",
        value: "Loading…",
      });
    }

    if (mapping.teMessage) {
      extraFields.push({ label: "TE message", value: mapping.teMessage });
    }

    return {
      displayName: mapping.displayName,
      standardFields,
      extraFields,
      instructions: mapping.instructions,
      note: mapping.note ?? null,
      noteLabel: mapping.noteLabel ?? null,
      statusLine: mapping.statusLine ?? null,
      workflowAction: mapping.workflowAction ?? null,
      additionalWorkflow: mapping.additionalWorkflow,
      faxRecipientNumber,
      tags: mapping.tags ?? null,
      stepOutputs: stepOutputs ?? null,
      ccDescription: ccDescription ?? null,
      processInstructionText,
    };
  }, [
    mapping,
    stepOutputs,
    ccDescription,
    reviewedContent,
    authLocation,
    staffLocation,
    locationPrefix,
    faxRecipientNumber,
    resolvedAssignment,
    isLocationLoading,
    patientFamilyName,
    patientGivenName,
    patientEhrSystemId,
    ehrDocumentId,
  ]);
}

export function useProcessedInstructions(task: Task, section: Section, enabled = true) {
  const {
    instructions,
    stepOutputs,
    ccDescription,
    isLoading: docLoading,
    isError: docError,
  } = useDocumentManagementInstructions(task.id, { enabled });

  const {
    authLocation,
    staffLocation,
    faxRecipientNumber,
    locationPrefix,
    resolvedAssignment,
    isLoading: locLoading,
  } = useFaxLocationAssignment(task.id, { enabled });

  // TODO: Remove this PA special case once facility assignment uses webECW.
  const resolvesFacilityAsync = instructions ? GROUPS_RESOLVING_FACILITY_ASYNC.has(instructions.group) : false;

  const data = useBuildInstructions(task, section, instructions, stepOutputs, ccDescription, {
    authLocation,
    staffLocation,
    locationPrefix,
    faxRecipientNumber,
    resolvedAssignment,
    isLoading: resolvesFacilityAsync && locLoading,
  });

  return {
    isLoading: docLoading || (!resolvesFacilityAsync && locLoading),
    isError: docError,
    data,
  };
}
